/**
 * Client-side frontmatter editing that preserves comments and key order, using
 * the `yaml` Document API. Only the properties panel uses this; the editor
 * itself treats the file as plain text.
 */
import { parseDocument, isMap, Document } from 'yaml';

const FM_RE = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;

export interface SplitRaw {
  fmText: string | null;
  body: string;
}

export function splitRaw(raw: string): SplitRaw {
  const m = raw.match(FM_RE);
  if (!m) return { fmText: null, body: raw };
  return { fmText: m[1], body: raw.slice(m[0].length) };
}

export function readFrontmatter(raw: string): Record<string, unknown> {
  const { fmText } = splitRaw(raw);
  if (fmText === null) return {};
  try {
    const doc = parseDocument(fmText);
    const v = doc.toJS() as unknown;
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/** Set (or delete with undefined) one top-level key, returning the new raw file. */
export function setFrontmatterKey(raw: string, key: string, value: unknown): string {
  const { fmText, body } = splitRaw(raw);
  let doc: Document = parseDocument(fmText ?? '');
  if (!isMap(doc.contents)) doc = new Document({});
  if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
    doc.delete(key);
  } else {
    doc.set(key, value);
  }
  const text = doc.toString({ lineWidth: 0 }).replace(/\n$/, '');
  if (!text.trim() || text.trim() === '{}') return body;
  return `---\n${text}\n---\n${body}`;
}

/** Turn a typed-in string into the value the schema expects. */
export function coerceValue(schema: Record<string, unknown> | undefined, s: string): unknown {
  const t = schema?.type;
  const types = Array.isArray(t) ? t : t ? [t] : [];
  if (s === '') return undefined;
  if (types.includes('integer') || types.includes('number')) {
    const n = Number(s);
    return Number.isFinite(n) ? n : s;
  }
  if (types.includes('boolean')) return s === 'true' ? true : s === 'false' ? false : s;
  return s;
}
